import * as React from "react"
import { Box, Flex, chakra } from "@chakra-ui/react"
import { useLocation } from "@reach/router"
import Logo from "./Logo"
import DemoButton from "./DemoButton"

// The v90 nav: a centered link cluster (Products dropdown, Pricing, Calculator,
// About) with a Property-managers / Owners toggle and the primary CTA on the
// right. Owner routes swap the center links for the owner pages and the CTA for
// "See My Estimate". (Enable Your Portfolio and Log in live in page heroes, per
// the artifact, not the nav.)
const productItems = [
  { href: "/guaranteed-rent", label: "Guaranteed Rent", desc: "On-time rent for your owners" },
  { href: "/rent-advance", label: "Rent Advance", desc: "Future rent as cash today" },
  { href: "/property-accounting", label: "Property Accounting", desc: "Full-service bookkeeping" },
]
const pmLinks = [
  { href: "/pricing", label: "Pricing" },
  { href: "/calculator", label: "Calculator" },
  { href: "/about", label: "About" },
]
const ownerLinks = [
  { href: "/owners/guaranteed-rent", label: "Guaranteed Rent" },
  { href: "/owners/rent-advance", label: "Rent Advance" },
  { href: "/owners/how-it-works", label: "How It Works" },
  { href: "/owners/pricing", label: "Pricing" },
]

const linkStyle = {
  fontSize: "0.85rem",
  fontWeight: 500,
  color: "#3f4a66",
  textDecoration: "none",
  whiteSpace: "nowrap" as const,
  _hover: { color: "brand.400" },
}

const Header: React.FC = () => {
  const location = useLocation()
  const isOwners = location.pathname.startsWith("/owners")
  const [scrolled, setScrolled] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [dropOpen, setDropOpen] = React.useState(false)

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const Toggle = (
    <Flex align="center" gap="9px">
      <chakra.a href="/" fontSize="0.75rem" fontWeight={isOwners ? 500 : 600} color={isOwners ? "#8B95B0" : "#1E2740"} opacity={isOwners ? 0.6 : 1} textDecoration="none" whiteSpace="nowrap" _hover={{ color: "#1E2740", opacity: 1 }}>
        Property managers
      </chakra.a>
      <Box w="1px" h="12px" bg="#E2E6F2" />
      <chakra.a href="/owners" fontSize="0.75rem" fontWeight={isOwners ? 600 : 500} color={isOwners ? "#1E2740" : "#8B95B0"} opacity={isOwners ? 1 : 0.6} textDecoration="none" whiteSpace="nowrap" _hover={{ color: "#1E2740", opacity: 1 }}>
        Owners
      </chakra.a>
    </Flex>
  )

  const primaryCta = isOwners ? (
    <chakra.a href="/owners/estimate" display="inline-flex" alignItems="center" px={5} py={2} fontSize="sm" fontWeight={600} color="white" bg="brand.400" borderRadius="full" textDecoration="none" _hover={{ bg: "brand.500" }}>
      See My Estimate
    </chakra.a>
  ) : (
    <DemoButton source="nav" size="sm" px={5} py={2} fontSize="sm" />
  )

  return (
    <Box
      as="nav"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={100}
      px={{ base: 6, md: 10 }}
      py={{ base: 4, md: "16px" }}
      bg={scrolled || menuOpen ? "rgba(255,255,255,0.97)" : "rgba(250,251,254,0.85)"}
      backdropFilter="blur(20px)"
      borderBottom="1px solid"
      borderColor="rgba(226,230,242,0.5)"
      boxShadow={scrolled ? "0 1px 12px rgba(30,39,64,0.06)" : "none"}
      transition="all 0.3s"
    >
      <Box display={{ base: "flex", md: "grid" }} gridTemplateColumns={{ md: "1fr auto 1fr" }} alignItems="center" justifyContent="space-between" gap="16px">
        <Box justifySelf="start">
          <Logo size="lg" to={isOwners ? "/owners" : "/"} />
        </Box>

        {/* Desktop center links */}
        <Flex display={{ base: "none", md: "flex" }} justifySelf="center" align="center" gap="26px">
          {isOwners ? (
            ownerLinks.map((l) => (
              <chakra.a key={l.href} href={l.href} {...linkStyle}>
                {l.label}
              </chakra.a>
            ))
          ) : (
            <>
              <Box
                position="relative"
                onMouseEnter={() => setDropOpen(true)}
                onMouseLeave={() => setDropOpen(false)}
                // Keyboard and touch: the trigger is a real button (click toggles,
                // focus opens), Escape closes, and tabbing out of the menu closes it.
                onKeyDown={(e) => {
                  if (e.key === "Escape") setDropOpen(false)
                }}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropOpen(false)
                }}
              >
                <chakra.button
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={dropOpen}
                  onClick={() => setDropOpen((v) => !v)}
                  onFocus={() => setDropOpen(true)}
                  display="inline-flex"
                  alignItems="center"
                  gap="5px"
                  bg="transparent"
                  border="none"
                  p={0}
                  fontFamily="inherit"
                  {...linkStyle}
                  cursor="pointer"
                >
                  Products
                  <Box as="span" transform={dropOpen ? "rotate(180deg)" : "none"} transition="transform 0.2s" display="inline-flex">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M6 9l6 6 6-6" /></svg>
                  </Box>
                </chakra.button>
                {/* hover bridge */}
                <Box position="absolute" top="100%" left={0} right={0} h="16px" />
                <Box
                  position="absolute"
                  top="calc(100% + 14px)"
                  left="50%"
                  transform="translateX(-50%)"
                  bg="white"
                  border="1px solid #E2E6F2"
                  borderRadius="14px"
                  boxShadow="0 16px 40px rgba(30,39,64,0.14)"
                  p="8px"
                  minW="240px"
                  display={dropOpen ? "flex" : "none"}
                  flexDirection="column"
                  gap="2px"
                  zIndex={60}
                >
                  {productItems.map((p) => (
                    <chakra.a key={p.href} href={p.href} display="block" px="12px" py="10px" borderRadius="9px" textDecoration="none" _hover={{ bg: "#F3F5FB" }}>
                      <Box fontSize="0.85rem" fontWeight={600} color="#1E2740">
                        {p.label}
                      </Box>
                      <Box fontSize="0.74rem" fontWeight={400} color="#8B95B0" mt="1px">
                        {p.desc}
                      </Box>
                    </chakra.a>
                  ))}
                </Box>
              </Box>
              {pmLinks.map((l) => (
                <chakra.a key={l.href} href={l.href} {...linkStyle}>
                  {l.label}
                </chakra.a>
              ))}
            </>
          )}
        </Flex>

        {/* Desktop right cluster */}
        <Flex display={{ base: "none", md: "flex" }} justifySelf="end" align="center" gap="16px">
          {!isOwners && Toggle}
          {primaryCta}
        </Flex>

        {/* Mobile cluster */}
        <Flex display={{ base: "flex", md: "none" }} align="center" gap="10px">
          {primaryCta}
          <chakra.button
            onClick={() => setMenuOpen((v) => !v)}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="5px"
            w="36px"
            h="36px"
            border="none"
            bg="transparent"
            cursor="pointer"
            p={0}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            <Box as="span" display="block" w="22px" h="2px" bg="#1E2740" borderRadius="2px" transition="all 0.2s" style={{ transform: menuOpen ? "translateY(7px) rotate(45deg)" : "none" }} />
            <Box as="span" display="block" w="22px" h="2px" bg="#1E2740" borderRadius="2px" transition="all 0.2s" opacity={menuOpen ? 0 : 1} />
            <Box as="span" display="block" w="22px" h="2px" bg="#1E2740" borderRadius="2px" transition="all 0.2s" style={{ transform: menuOpen ? "translateY(-7px) rotate(-45deg)" : "none" }} />
          </chakra.button>
        </Flex>
      </Box>

      {/* Mobile dropdown */}
      {menuOpen && (
        <Box display={{ base: "flex", md: "none" }} flexDirection="column" pt="16px" pb="10px" borderTop="1px solid" borderColor="rgba(226,230,242,0.6)" mt="12px" gap="2px">
          {(isOwners ? ownerLinks : [...productItems, ...pmLinks]).map((l) => (
            <chakra.a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} display="block" px="4px" py="10px" color="#1E2740" fontSize="0.95rem" fontWeight={500} textDecoration="none" borderRadius="8px" _hover={{ color: "brand.400", bg: "rgba(103,145,203,0.06)" }}>
              {l.label}
            </chakra.a>
          ))}
          {!isOwners && (
            <Box pt="12px" mt="6px" borderTop="1px solid rgba(226,230,242,0.6)">
              {Toggle}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default Header
